import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppWindow, Zap, TrendingUp, RefreshCw, Download, AlertTriangle, Activity } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  getAdminAnalytics,
  getAnalyticsTrends,
  getAppRuntime,
  getAppLifecycleTrends,
  type AdminAnalytics,
  type TrendPoint,
  type AppRuntimeBucket,
  type AppLifecycleTrend,
} from '../../../lib/api'
import { getAdminAppsPath } from '../../../lib/adminRoutes'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import StatCard from '../../../components/analytics/StatCard'
import TrendChart from '../../../components/analytics/TrendChart'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'
import { AnalyticsNav } from './AnalyticsOverview'

export default function AppAnalytics() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<string>('30d')
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [trends, setTrends] = useState<TrendPoint[]>([])
  const [runtimeBuckets, setRuntimeBuckets] = useState<AppRuntimeBucket[]>([])
  const [lifecycleTrends, setLifecycleTrends] = useState<AppLifecycleTrend[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [analyticsData, trendsData, runtimeData, lifecycleData] = await Promise.all([
        getAdminAnalytics(period),
        getAnalyticsTrends(period),
        getAppRuntime(),
        getAppLifecycleTrends(period),
      ])
      setAnalytics(analyticsData)
      setTrends(trendsData)
      setRuntimeBuckets(runtimeData)
      setLifecycleTrends(lifecycleData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load app analytics')
      throw err
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useAutoRefresh(fetchData, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  // Pivot the flat lifecycleTrends array into the chart-friendly shape recharts
  // expects: one row per date with one column per action key (e.g.
  // {date, app_create, app_start, app_stop, ...}).
  const lifecycleData = useMemo(() => {
    const byDate: Record<string, Record<string, number | string>> = {}
    for (const t of lifecycleTrends) {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date }
      byDate[t.date][t.action] = t.event_count
    }
    return Object.values(byDate).sort((a, b) =>
      (a.date as string).localeCompare(b.date as string)
    )
  }, [lifecycleTrends])

  const lifecycleActions = useMemo(
    () => [...new Set(lifecycleTrends.map((t) => t.action))],
    [lifecycleTrends]
  )

  const lifecycleLines = useMemo(
    () =>
      lifecycleActions.map((action, i) => ({
        dataKey: action,
        label: action,
        color: CHART_PALETTE[i % CHART_PALETTE.length],
      })),
    [lifecycleActions]
  )

  function handleExportCSV() {
    if (!analytics || analytics.top_apps.length === 0) return
    const csv = arrayToCSV(analytics.top_apps, [
      { key: 'app_name', label: 'App Name' },
      { key: 'event_count', label: 'Events' },
      { key: 'owner_email', label: 'Owner' },
    ])
    downloadCSV(csv, `analytics-apps-${period}.csv`)
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">App Analytics</h1>
          <p className="text-muted-foreground">
            App lifecycle stats, deploy trends, and runtime distribution
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <button
            onClick={handleExportCSV}
            disabled={!analytics || analytics.top_apps.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <AnalyticsNav />

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !analytics ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : analytics ? (
        <div className="space-y-8">
          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            <StatCard
              label="Total Apps"
              value={analytics.platform.total_apps}
              icon={AppWindow}
            />
            <StatCard
              label="Running Apps"
              value={analytics.platform.running_apps}
              icon={Zap}
            />
            <StatCard
              label="Apps in Error"
              value={analytics.platform.apps_in_error}
              icon={AlertTriangle}
            />
            <StatCard
              label="Deploy Attempts"
              value={analytics.platform.deploy_attempts}
              icon={Activity}
            />
            <StatCard
              label="Running Ratio"
              value={`${analytics.platform.running_app_ratio}%`}
              icon={TrendingUp}
            />
          </div>
          <p className="text-sm text-muted-foreground -mt-4">
            Running Ratio shows current app availability. Deploy Attempts counts `app_start` and
            `app_rebuild` actions for the selected period.
          </p>

          {/* Runtime distribution chart */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-1">Runtime Distribution</h2>
            <p className="text-sm text-muted-foreground mb-4">
              How long running apps have been active
            </p>
            {runtimeBuckets.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                No running apps
              </div>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={runtimeBuckets}
                    margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="bucket"
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickLine={false}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickLine={false}
                      axisLine={false}
                      width={45}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        borderColor: 'hsl(var(--border))',
                        borderRadius: '0.5rem',
                        color: 'hsl(var(--card-foreground))',
                      }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {runtimeBuckets.map((_, index) => (
                        <Cell
                          key={index}
                          fill={CHART_PALETTE[index % CHART_PALETTE.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Trend charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4">Deploys Over Time</h2>
              <TrendChart
                data={trends}
                lines={[
                  { dataKey: 'deploys', label: 'Deploys', color: CHART_PALETTE[0] },
                ]}
                height={250}
              />
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4">Total Apps Over Time</h2>
              <TrendChart
                data={trends}
                lines={[
                  { dataKey: 'total_apps', label: 'Total Apps', color: CHART_PALETTE[1] },
                ]}
                height={250}
              />
            </div>
          </div>

          {/* App lifecycle action breakdown — moved here from the Feature
              Adoption page where it drowned out the smaller buckets. */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-1">App Lifecycle Actions</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Daily count per action: create, clone, update, start, stop, restart, rebuild, delete, restore, permanent delete.
            </p>
            {lifecycleData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                No lifecycle activity for this period
              </div>
            ) : (
              <TrendChart data={lifecycleData} lines={lifecycleLines} height={320} />
            )}
          </div>

          {/* Top apps table */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Most Active Apps</h2>
            {analytics.top_apps.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No app activity for this period
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        App Name
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Events
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Owner
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_apps.map((app) => (
                      <tr
                        key={app.app_name}
                        className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors cursor-pointer"
                        onClick={() => navigate(getAdminAppsPath({ query: app.app_name }))}
                      >
                        <td className="py-3 px-4 font-medium">{app.app_name}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {app.event_count}
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {app.owner_email}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          No analytics data available
        </div>
      )}
    </div>
  )
}
